import { Modal, Select, Tooltip } from 'antd';
import { UseFormReturn, Controller } from 'react-hook-form';
import { SelectInput, TextInput } from '../../../components';
import { IApprovalModalConfig } from '../../../types';
import {
  REVIEW_TYPE_DROPDOWN_OPTIONS,
  CREATOR_LEVEL,
} from '../../TravelRequest/constants/travelreq.constants';
import { IApprovalForm } from '../../TravelRequest/constants/travelreq.interface';
import { useMemo } from 'react';
import { useGetAllUsersQuery } from '../../../store';
import { IAntdDropdown } from '../../Requisitions/constants/requisition.interface';

interface IApprovalConfirmationModalProps {
  config: IApprovalModalConfig;
  inputForm: UseFormReturn<IApprovalForm>;
  isOpen: boolean;
  isLoading: boolean;
  onConfirm: (args: IApprovalForm) => void;
  onCancel: () => void;
  width?: number;
}

export function ApprovalConfirmationModal({
  config,
  inputForm,
  isOpen,
  isLoading,
  onConfirm,
  onCancel,
  width = 400,
}: IApprovalConfirmationModalProps) {
  const { data: usersData } = useGetAllUsersQuery(null);

  const reviewerDropdown = useMemo(() => {
    if (!config.reviewers?.length) return [];

    // Sort by approvalLevel (ascending order)
    const sortedReviewers = [...config.reviewers].sort((a, b) => {
      const levelA = a?.approvalLevel ?? CREATOR_LEVEL;
      const levelB = b?.approvalLevel ?? CREATOR_LEVEL;
      return levelA - levelB;
    });

    return sortedReviewers.map(item => {
      const approvalLevel = item?.approvalLevel;
      const approver = item?.approver;
      const email = approver?.EmailID || '';
      const fullName = approver?.FullName || '';
      const employeeCode = approver?.EmployeeCode || '';

      // Format label: "Creator - FullName (EmployeeCode)" or "L1 - FullName (EmployeeCode)"
      let levelLabel = '';
      if (approvalLevel === CREATOR_LEVEL) {
        levelLabel = 'Creator';
      } else {
        levelLabel = `L${approvalLevel}`;
      }

      // Build label with EmployeeCode in parentheses
      const label = employeeCode
        ? `${levelLabel} - ${fullName} (${employeeCode})`
        : `${levelLabel} - ${fullName}`;

      return {
        value: approvalLevel,
        label: label,
        email: email, // Store email for tooltip
      };
    });
  }, [config]);

  const ccUsersDropdown = useMemo(() => {
    if (usersData?.data) {
      return (usersData.data || []).map((user: any) => {
        const employeeCode = user.EmployeeCode || '';
        const fullName = user.FullName || '';
        const email = user.EmailID || '';

        // Format label: "EmployeeCode - FullName"
        const label = employeeCode ? `${employeeCode} - ${fullName}` : fullName;

        return {
          value: user.ID,
          label: label,
          email: email, // Store email for tooltip
        };
      });
    }
    return [];
  }, [usersData]) as { value: number; label: string; email: string }[];

  return (
    <Modal
      width={width}
      title={config.title}
      open={isOpen}
      onOk={inputForm.handleSubmit(onConfirm)}
      onCancel={onCancel}
      okText="Submit"
      maskClosable={!isLoading}
      okButtonProps={{ loading: isLoading, disabled: isLoading }}
      cancelButtonProps={{ disabled: isLoading }}
      closable={false}
    >
      <TextInput
        name={'remarks'}
        control={inputForm.control}
        rules={{
          required: '* This field is required',
        }}
        label={'Remarks'}
        formState={inputForm.formState}
        config={{
          autoSize: { minRows: 3, maxRows: 3 },
        }}
        variant="textarea"
      />
      <div className="mt-2"></div>
      {config.showReviewers ? (
        <div className="mt-2 flex flex-col gap-2">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-text-100 whitespace-nowrap">
              Send for review to *
            </label>
            <Controller
              name={'toLevel'}
              control={inputForm.control}
              rules={{ required: '* This field is required' }}
              render={({ field: { onChange, onBlur, value, ref } }) => (
                <>
                  <Select
                    placeholder="Choose from options"
                    optionFilterProp="label"
                    optionLabelProp="label"
                    value={value}
                    onChange={selected => {
                      if (selected === undefined) {
                        onChange(null);
                      } else {
                        onChange(selected);
                      }
                    }}
                    onBlur={onBlur}
                    ref={ref}
                    showSearch
                  >
                    {reviewerDropdown.map(option => (
                      <Select.Option
                        key={option.value}
                        label={option.label}
                        value={option.value}
                      >
                        <Tooltip
                          title={option.email || 'No email available'}
                          placement="right"
                        >
                          <span>{option.label}</span>
                        </Tooltip>
                      </Select.Option>
                    ))}
                  </Select>
                  {inputForm.formState.errors.toLevel && (
                    <div className="text-xs text-red-500">
                      {inputForm.formState.errors.toLevel.message}
                    </div>
                  )}
                </>
              )}
            />
          </div>

          <SelectInput
            name={'reviewType'}
            control={inputForm.control}
            rules={{ required: '* This field is required' }}
            label={'Review Type *'}
            formState={inputForm.formState}
            config={{
              showSearch: true,
              placeholder: 'Choose from options',
              optionFilterProp: 'label',
              optionLabelProp: 'label',
              options: REVIEW_TYPE_DROPDOWN_OPTIONS,
            }}
          />

          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-text-100 whitespace-nowrap">
              CC Users (Max 10)
            </label>
            <Controller
              name={'ccUsers'}
              control={inputForm.control}
              rules={{
                validate: (value: number[]) => {
                  const maxLimit = 10; // Safe limit for CC users
                  if (value && value.length > maxLimit) {
                    return `Maximum ${maxLimit} users can be selected for CC`;
                  }
                  return true;
                },
              }}
              render={({ field: { onChange, onBlur, value, ref } }) => {
                const maxLimit = 10;
                return (
                  <>
                    <Select
                      mode="multiple"
                      allowClear
                      maxTagCount={2}
                      placeholder="Select users to CC (max 10)"
                      showSearch
                      optionFilterProp="label"
                      optionLabelProp="label"
                      value={value || []}
                      onChange={newValue => {
                        // Limit selections to maxLimit
                        if (newValue.length <= maxLimit) {
                          onChange(newValue);
                        } else {
                          // Keep only the first maxLimit selections
                          onChange(newValue.slice(0, maxLimit));
                        }
                      }}
                      onBlur={onBlur}
                      ref={ref}
                    >
                      {ccUsersDropdown.map(option => (
                        <Select.Option
                          key={option.value}
                          label={option.label}
                          value={option.value}
                        >
                          <Tooltip
                            title={option.email || 'No email available'}
                            placement="right"
                          >
                            <span>{option.label}</span>
                          </Tooltip>
                        </Select.Option>
                      ))}
                    </Select>
                    {inputForm.formState.errors.ccUsers && (
                      <div className="text-xs text-red-500">
                        {inputForm.formState.errors.ccUsers.message}
                      </div>
                    )}
                  </>
                );
              }}
            />
          </div>
        </div>
      ) : (
        <></>
      )}
      <div className="py-2"></div>
      {config.body}
    </Modal>
  );
}

export default ApprovalConfirmationModal;
